import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { COMPANY } from "@/data/site";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: July 2026",
    sections: [
      {
        h: "Who We Are",
        p: `${COMPANY.name} (Company Number ${COMPANY.companyNumber}) is a UK-registered technology company. This policy explains how we handle personal information submitted through ${COMPANY.website}.`,
      },
      {
        h: "What We Collect",
        p: "When you submit our contact form, we collect your name, business name, email address, phone number, the service you are interested in, your budget range and your message. We use this information solely to respond to your enquiry and prepare a tailored proposal.",
      },
      {
        h: "How We Use Your Information",
        p: "Your information is used only to communicate with you about your enquiry and our services. We do not sell, rent or share your personal information with third parties for their marketing purposes.",
      },
      {
        h: "Data Retention & Security",
        p: "Enquiries are stored securely and retained only for as long as needed to handle your enquiry and any resulting business relationship. We apply appropriate technical and organisational measures to protect your data.",
      },
      {
        h: "Your Rights",
        p: `Under UK GDPR, you have the right to access, correct or request deletion of your personal data. To exercise these rights, contact us at ${COMPANY.email}.`,
      },
    ],
  },
  cookies: {
    title: "Cookie Policy",
    updated: "Last updated: July 2026",
    sections: [
      {
        h: "About This Policy",
        p: `This Cookie Policy explains how ${COMPANY.name} (${COMPANY.website}) uses cookies and similar technologies.`,
      },
      {
        h: "Cookies We Use",
        p: "This website currently uses only essential technologies required for the site to function and load efficiently. We do not use advertising or third-party tracking cookies.",
      },
      {
        h: "Managing Cookies",
        p: "You can control or delete cookies through your browser settings at any time. Disabling cookies will not affect the core functionality of this website.",
      },
      {
        h: "Changes",
        p: `If we introduce analytics or additional cookies in the future, this policy will be updated and, where required, your consent will be requested. Questions? Contact ${COMPANY.email}.`,
      },
    ],
  },
  terms: {
    title: "Terms & Conditions",
    updated: "Last updated: July 2026",
    sections: [
      {
        h: "Introduction",
        p: `These terms govern your use of ${COMPANY.website}, operated by ${COMPANY.name} (Company Number ${COMPANY.companyNumber}), registered in the United Kingdom. By using this website you accept these terms.`,
      },
      {
        h: "Website Content",
        p: "Content on this website is provided for general information about our services. It does not constitute a formal offer, quotation or contract. Project scopes, timelines and pricing are agreed individually in writing.",
      },
      {
        h: "Intellectual Property",
        p: `All content, branding and design on this website are the property of ${COMPANY.name} and may not be reproduced without written permission.`,
      },
      {
        h: "Limitation of Liability",
        p: "We take reasonable care to keep the information on this website accurate, but we provide it without warranties of any kind. To the extent permitted by law, we are not liable for any loss arising from use of this website.",
      },
      {
        h: "Contact",
        p: `For any questions about these terms, contact us at ${COMPANY.email}.`,
      },
    ],
  },
};

export default function Legal({ page }) {
  const content = CONTENT[page] || CONTENT.privacy;

  useEffect(() => {
    document.title = `${content.title} | ${COMPANY.name}`;
    window.scrollTo(0, 0);
    return () => {
      document.title = "Prata Technologies Ltd | AI Automation, WhatsApp AI Chatbots & Digital Solutions UK";
    };
  }, [content.title]);

  return (
    <>
      <Header />
      <main className="bg-navy-950 pb-24 pt-36 md:pt-44" data-testid={`legal-${page}`}>
        <div className="mx-auto max-w-3xl px-6 md:px-12">
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 text-sm text-slate-400 transition-colors duration-300 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </Link>
          <h1 className="mt-8 font-display text-4xl font-medium tracking-tight text-white sm:text-5xl">
            {content.title}
          </h1>
          <p className="mt-3 text-xs uppercase tracking-[0.18em] text-slate-500">{content.updated}</p>
          <div className="mt-12 space-y-10">
            {content.sections.map((section) => (
              <section key={section.h}>
                <h2 className="font-display text-xl font-medium text-white">{section.h}</h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{section.p}</p>
              </section>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
